import * as React from 'react';
import axios from '../../utils/axios';
import { Num, CABECALHO_TABELA_COMPACTO, BotaoExportar } from './index';
import Modal, { classesEntrada } from './Modal';

// Quantas linhas o modal desenha; busca e Excel usam a lista inteira.
const LINHAS_NA_TELA = 300;

/* ============================================================================
   "Ver grupos" / "Ver dispositivos" de um aplicativo - visual novo.

   Substitui pages/dashboard/aplicativos/components/ModalDetalheApp.tsx, que era
   Dialog do MUI + DataGrid do MUI X. Somente leitura, dois GET, nenhuma escrita -
   por isso foi o primeiro dos modais antigos a migrar: risco zero e e o mais
   clicado, porque sai dos contadores da listagem.

   Contratos, os mesmos de antes:
     GET aplicativos/mostrarGrupo/:packageName   -> [{ GRUPO_ID, GRUPO_NOME, DISPOSITIVOS }]
     GET aplicativos/dispositivos/:packageName   -> [{ deviceId, nome, modelo, imei, grupo, regional, status }]

   DUAS COISAS QUE MELHORARAM, e nao sao estilo:

   1. O TOTAL APARECE. O DataGrid mostrava as linhas e nada mais; a pergunta que
      leva alguem a abrir isto e "em quantos?" e "quantos aparelhos?". Agora o
      rodape soma - e no modo grupos ele soma tambem os dispositivos, que e o
      alcance real do aplicativo.

   2. A BUSCA FILTRA E DIZ QUANTO SOBROU. Antes filtrava em silencio, e com uma
      lista longa nao havia como saber se o termo achou 2 de 40 ou 40 de 40.

   A busca continua varrendo TODOS os campos da linha (`Object.values`), como a
   anterior - e o comportamento que faz procurar por IMEI, por modelo ou por nome
   de grupo na mesma caixa.
   ============================================================================ */

type Modo = 'grupos' | 'dispositivos';

const COLUNAS: Record<Modo, Array<{ campo: string; rotulo: string; largura: string; mono?: boolean; dir?: boolean }>> = {
  grupos: [
    { campo: 'GRUPO_NOME', rotulo: 'Grupo', largura: 'minmax(220px,2.4fr)' },
    { campo: 'DISPOSITIVOS', rotulo: 'Dispositivos', largura: '140px', dir: true },
  ],
  dispositivos: [
    { campo: 'nome', rotulo: 'Nome', largura: 'minmax(160px,1.6fr)' },
    { campo: 'modelo', rotulo: 'Modelo', largura: '130px' },
    { campo: 'imei', rotulo: 'IMEI', largura: '150px', mono: true },
    { campo: 'grupo', rotulo: 'Grupo', largura: 'minmax(150px,1.4fr)' },
    { campo: 'regional', rotulo: 'Regional', largura: '140px' },
    { campo: 'status', rotulo: 'Status', largura: '110px' },
  ],
};

function rotuloStatus(v: unknown) {
  if (v === 1 || v === '1') return 'Ativo';
  if (v === 0 || v === '0') return 'Inativo';
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

export default function ModalDetalheApp({
  aberto,
  modo,
  packageName,
  appName,
  onFechar,
}: {
  aberto: boolean;
  modo: Modo;
  packageName: string;
  appName?: string;
  onFechar: () => void;
}) {
  const [linhas, setLinhas] = React.useState<any[]>([]);
  const [carregando, setCarregando] = React.useState(false);
  const [falhou, setFalhou] = React.useState(false);
  const [busca, setBusca] = React.useState('');

  React.useEffect(() => {
    if (!aberto || !packageName) return;
    let ativo = true;
    setFalhou(false);
    setBusca('');
    setCarregando(true);

    const url =
      modo === 'grupos'
        ? `aplicativos/mostrarGrupo/${encodeURIComponent(packageName)}`
        : `aplicativos/dispositivos/${encodeURIComponent(packageName)}`;

    axios
      .get<any[]>(url)
      .then((r) => ativo && setLinhas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        /* FALHA NAO E "ALCANCE ZERO" (28/09/2026).

           Zerar as linhas aqui fazia o modal abrir dizendo
           "0 grupo(s) - alcance de 0 dispositivo(s)", com o botao de Excel
           habilitado para exportar esse zero. A leitura natural e "este
           aplicativo nao esta vinculado a ninguem", que e o oposto do que se
           sabe: nao se sabe nada, a consulta falhou. */
        if (!ativo) return;
        setLinhas([]);
        setFalhou(true);
      })
      .finally(() => ativo && setCarregando(false));

    return () => {
      ativo = false;
    };
  }, [aberto, modo, packageName]);

  const filtradas = React.useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return linhas;
    return linhas.filter((r) =>
      Object.values(r).some((v) => String(v ?? '').toLowerCase().includes(termo))
    );
  }, [linhas, busca]);

  const colunas = COLUNAS[modo];
  const grade = colunas.map((c) => c.largura).join(' ');

  const totalDispositivos =
    modo === 'grupos' ? linhas.reduce((s, r) => s + (Number(r.DISPOSITIVOS) || 0), 0) : linhas.length;

  return (
    <Modal
      aberto={aberto}
      titulo={modo === 'grupos' ? 'Grupos do aplicativo' : 'Dispositivos do aplicativo'}
      descricao={appName ? `${appName} · ${packageName}` : packageName}
      icone={modo === 'grupos' ? '🗂️' : '📱'}
      largura="max-w-[900px]"
      onFechar={onFechar}
    >
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={modo === 'grupos' ? 'Filtrar por grupo' : 'Nome, modelo, IMEI, grupo ou regional'}
          className={`${classesEntrada} max-w-[320px]`}
        />
        <span className="text-[12px] text-muted-foreground">
          {falhou ? (
            <span className="font-semibold text-warn">
              não foi possível carregar — o número abaixo não vale
            </span>
          ) : busca.trim() ? (
            <>
              <Num className="font-bold text-ink">{filtradas.length}</Num> de{' '}
              <Num>{linhas.length}</Num>
            </>
          ) : (
            <>
              <Num className="font-bold text-ink">{linhas.length}</Num>{' '}
              {modo === 'grupos' ? 'grupo(s)' : 'dispositivo(s)'}
              {modo === 'grupos' && (
                <>
                  {' · alcance de '}
                  <Num className="font-bold text-ink">{totalDispositivos.toLocaleString('pt-BR')}</Num>{' '}
                  dispositivo(s)
                </>
              )}
            </>
          )}
        </span>

        {/* Exporta o que esta na tela: se ha filtro digitado, sao as linhas
            filtradas - o numero no rotulo confirma qual dos dois. Esta lista vem
            inteira do backend numa chamada, entao aqui nao ha o corte de pagina que
            o historico de logs tem. */}
        {/* Exportar uma lista que falhou produz planilha com zero linha e cara
            de resultado - por isso o botao sai da tela enquanto houver falha. */}
        <BotaoExportar
          className="ml-auto"
          desabilitado={falhou}
          rotulo={`Excel (${filtradas.length})`}
          nomeArquivo={`${modo === 'grupos' ? 'grupos' : 'dispositivos'}-${packageName || 'aplicativo'}`}
          linhas={() =>
            filtradas.map((r) => {
              const registro: Record<string, any> = {};
              colunas.forEach((c) => {
                registro[c.rotulo] = (r as any)[c.campo] ?? '';
              });
              return registro;
            })
          }
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-line">
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            <div
              className={CABECALHO_TABELA_COMPACTO}
              style={{ gridTemplateColumns: grade }}
            >
              {colunas.map((c) => (
                <div key={c.campo} className={c.dir ? 'text-right' : undefined}>
                  {c.rotulo}
                </div>
              ))}
            </div>

            {/* Com 21.955 aparelhos (WhatsApp, medido), desenhar TODAS as linhas
                travava o navegador. A tela mostra as primeiras LINHAS_NA_TELA; a
                busca, a contagem e o Excel continuam sobre a lista inteira. */}
            {!carregando && filtradas.length > LINHAS_NA_TELA && (
              <div className="border-b border-line bg-surface-2 px-3 py-1.5 text-[11.5px] text-muted-foreground">
                Mostrando {LINHAS_NA_TELA.toLocaleString('pt-BR')} de {filtradas.length.toLocaleString('pt-BR')} — use a
                busca para achar um item ou o Excel para a lista completa.
              </div>
            )}
            <div className="max-h-[52vh] overflow-y-auto">
              {carregando && <div className="p-6 text-center text-[12px] text-muted-foreground">Carregando…</div>}

              {/* Com falha o aviso de erro ja aparece acima: sem `!falhou` saia tambem
                  "nao esta em nenhum grupo", o contrario do que se sabe. */}
              {!carregando && !falhou && !filtradas.length && (
                <div className="p-6 text-center text-[12px] text-muted-foreground">
                  {linhas.length
                    ? 'Nada encontrado com esse filtro.'
                    : modo === 'grupos'
                      ? 'Este aplicativo não está em nenhum grupo — fica catalogado e não chega a dispositivo nenhum.'
                      : 'Nenhum dispositivo com este aplicativo.'}
                </div>
              )}

              {!carregando &&
                filtradas.slice(0, LINHAS_NA_TELA).map((r, i) => (
                  <div
                    key={(modo === 'grupos' ? r.GRUPO_ID : r.deviceId) ?? i}
                    className="grid items-center border-b px-3 py-1.5 hover:bg-surface-2"
                    style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)' }}
                  >
                    {colunas.map((c) => {
                      const bruto = r[c.campo];
                      const valor = c.campo === 'status' ? rotuloStatus(bruto) : bruto;
                      const conteudo =
                        valor === null || valor === undefined || valor === '' ? '—' : String(valor);

                      if (c.dir || c.mono) {
                        return (
                          <Num
                            key={c.campo}
                            className={`block truncate text-[12px] text-muted-foreground ${c.dir ? 'text-right' : ''}`}
                          >
                            {conteudo}
                          </Num>
                        );
                      }

                      return (
                        <div key={c.campo} className="min-w-0 pr-2">
                          <span className="block truncate text-[12px]" title={conteudo}>
                            {conteudo}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
